import { Spin, Typography, Row, Col, Flex, Steps, App, Alert, Tooltip } from "antd";
import BackButton from "../../components/CustomButton/BackButton";
import CustomCard from "../../components/CustomCard/CustomCard";
import DetailsHeader from "../../components/Details/DetailsHeader";
import {
    TITLE_TXT,
    GLOBAL_CONSTANTS,
    BTN_TXT,
    ROLE_MODAL_WIDTHS,
} from "../../constants/constants";
import { getAssignTxn, settleTxn, settlePreview } from "../../redux/slices/settlementSlice";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { formatCurrency } from "../../helpers/helpers";
import DealDetailNoSpace from "../../components/Details/DealDetailNoSpace";
import DealDetail from "../../components/Details/DealDetail";
import { InfoCircleOutlined } from "@ant-design/icons";
import InfoModal from "../../components/CustomModal/InfoModal";
import SettleDealTable from "./Components/SettleDealTable";
import FormFooter from "../../components/CustomForms/FormFooter";
import SettleDealPreview from "./Components/SettleDealPreview";
import CustomButton from "../../components/CustomButton/Button";
import { PERM_CODE } from "../../constants/PermissionCodes";
import "./Settlements.scss";

const { Text } = Typography;

const SettlementDetails = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { message } = App.useApp();
    const { txnId } = useParams();
    const { user } = useSelector((state) => state.auth);
    const { settleTxnSummary, loading, settleLoading, error, settleBreakUpLoading } = useSelector(
        (state) => state?.settlement
    );
    const [modal, setModal] = useState({
        type: null,
        data: null,
    });
    const [selectedRowKeys, setSelectedRowKeys] = useState([])
    const [multiSettleBreakUp, setMultiSettleBreakUp] = useState(null)
    const userPrivileges = user?.privilege?.map((p) => p.privilegeCode) || [];

    const repayAmounts = useMemo(() => {
        const deals = settleTxnSummary?.deals || [];
        const txnAmount = Number(settleTxnSummary?.TxnDetails?.amount || 0);

        let remainingAmount = txnAmount;
        const amounts = {};

        // Same cascade order as backend multiSettle: partially settled first, then due date
        const selectedDeals = deals
            .filter((deal) => selectedRowKeys.includes(deal.dealId))
            .sort((a, b) => {
                const aPartial = a.isPartiallySettled ? 0 : 1;
                const bPartial = b.isPartiallySettled ? 0 : 1;
                if (aPartial !== bPartial) return aPartial - bPartial;
                return String(a.dueDate || "").localeCompare(String(b.dueDate || ""));
            });

        selectedDeals.forEach((deal) => {
            if (remainingAmount <= 0) return;

            const openBalance = Number(deal.openBalance || 0);
            const settleAmount = Math.min(openBalance, remainingAmount);

            amounts[deal.dealId] = settleAmount;
            remainingAmount -= settleAmount;
        });

        return amounts;
    }, [settleTxnSummary, selectedRowKeys]);

    useEffect(() => {
        dispatch(getAssignTxn(txnId));
    }, [dispatch, txnId]);

    useEffect(() => {
        const deals = settleTxnSummary?.deals || [];
        const txnAmount = Number(settleTxnSummary?.TxnDetails?.amount || 0);

        if (!deals.length || !txnAmount) return;

        let selected = [];
        let remainingAmount = txnAmount;

        // If backend already sends deals by due date, no sorting needed
        deals.forEach((deal) => {
            if (remainingAmount <= 0) return;

            const openBalance = Number(deal.openBalance || 0);

            selected.push(deal.dealId);

            const settleAmount = Math.min(
                openBalance,
                remainingAmount
            );

            remainingAmount -= settleAmount;
        });

        setSelectedRowKeys((prev) => {
            if (
                prev.length === selected.length &&
                prev.every((id, index) => id === selected[index])
            ) {
                return prev;
            }
            return selected;
        });
    }, [settleTxnSummary]);

    const closeModal = useCallback(
        () => setModal({ type: null, data: null }),
        []);

    const openModal = useCallback((type, settle) => {
        setModal({ type, data: settle });
    }, []);

    const totalAvailable = Number(settleTxnSummary?.TxnDetails?.amount || 0);

    const totalUsed = Object.values(repayAmounts).reduce(
        (sum, val) => sum + (Number(val) || 0),
        0
    );

    const remAvailAmtToSettle = Math.max(totalAvailable - totalUsed, 0);

    const openDeals = settleTxnSummary?.deals || [];
    const allDealsFullySettled = openDeals.every(
        (deal) => Number(repayAmounts[deal.dealId] || 0) >= Number(deal.openBalance || 0) - 1
    );
    const leftoverToBusiness = allDealsFullySettled ? remAvailAmtToSettle : 0;

    // Block settle while unallocated amount remains and other open deals still exist
    // (same case as the yellow warning Alert below).
    const canSettle =
        selectedRowKeys.length > 0 &&
        totalUsed <= totalAvailable &&
        (remAvailAmtToSettle <= 1 || allDealsFullySettled);

    // Enable breakdown when each selected deal has enough open balance to settle.
    // Do not gate on the naive txn-slice allocation (repayAmounts) — multiSettle
    // cascades leftover business payout into later deals, so a 6th deal can still
    // receive funds even when repayAmounts shows 0 for it.
    const canViewBreakdown = useMemo(
        () => selectedRowKeys.length > 0,
        [selectedRowKeys]
    );

    const handleViewSelectedBreakdown = useCallback(async () => {
        if (!canViewBreakdown) return;

        const orderedDealIds = (settleTxnSummary?.deals || [])
            .filter((deal) => selectedRowKeys.includes(deal.dealId))
            .sort((a, b) => {
                const aPartial = a.isPartiallySettled ? 0 : 1;
                const bPartial = b.isPartiallySettled ? 0 : 1;
                if (aPartial !== bPartial) return aPartial - bPartial;
                return String(a.dueDate || "").localeCompare(String(b.dueDate || ""));
            })
            .map((deal) => deal.dealId);

        const response = await dispatch(
            settlePreview({
                dealIds: orderedDealIds,
                amount: settleTxnSummary?.TxnDetails?.amount,
                txnDate: settleTxnSummary?.TxnDetails?.txnCreditedDate,
            })
        );

        if (response.payload?.statusCode !== 201) {
            message.error(
                response.payload?.error ||
                response.payload?.message ||
                "Failed to load settlement breakdown"
            );
            return;
        }

        setMultiSettleBreakUp(response.payload?.response);
        openModal("settlePreview", selectedRowKeys);
    }, [
        canViewBreakdown,
        dispatch,
        message,
        openModal,
        selectedRowKeys,
        settleTxnSummary,
    ]);

    const handleSettleDeals = async () => {
        if (selectedRowKeys.length === 0) return;

        const payload = {
            dealIds: selectedRowKeys,
            txnId: txnId || settleTxnSummary?.TxnDetails?.txnId,
        };

        const response = await dispatch(settleTxn(payload));
        if (response.payload?.statusCode === 201) {
            const settleResult = response.payload?.response;
            const successMsg =
                typeof settleResult === "string"
                    ? settleResult
                    : settleResult?.message ||
                    "Settlement completed successfully";
            message.success(successMsg);
            navigate(-1);
            setSelectedRowKeys([]);
        }
    };

    const previewTitle = `Settlement Breakdown`;

    return (
        <div className="settlement-details-container">
            <BackButton />
            <CustomCard>
                <Spin spinning={loading || settleBreakUpLoading}>
                    <DetailsHeader
                        title={TITLE_TXT.TRANSC_SUM}
                        titleStyle="txn-sum-title"
                    />
                    <div className="details-container-wraper">
                        <div className="details-container">
                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={8}>
                                    <div className="detail-col-container">
                                        <DealDetail
                                            label="Transaction ID"
                                            value={
                                                settleTxnSummary?.TxnDetails?.txnId ||
                                                GLOBAL_CONSTANTS.NA
                                            }
                                        />
                                        <DealDetail
                                            label="UTR"
                                            value={
                                                settleTxnSummary?.TxnDetails?.utr || GLOBAL_CONSTANTS.NA
                                            }
                                        />
                                        <DealDetail
                                            label="Credited Date"
                                            value={
                                                settleTxnSummary?.TxnDetails?.txnCreditedDate
                                                    ? settleTxnSummary.TxnDetails.txnCreditedDate.split("-").reverse().join("/")
                                                    : GLOBAL_CONSTANTS.NA
                                            }
                                        />
                                    </div>
                                </Col>
                                <Col xs={24} md={16}>
                                    <div className="detail-col-container">
                                        <DealDetail
                                            label="Business Name"
                                            value={
                                                settleTxnSummary?.TxnDetails?.orgName ||
                                                GLOBAL_CONSTANTS.NA
                                            }
                                        />
                                        <DealDetail
                                            label="Available Amount to Settle"
                                            value={
                                                formatCurrency(remAvailAmtToSettle) ||
                                                GLOBAL_CONSTANTS.NA
                                            }
                                        />
                                        <DealDetail
                                            label="Credited Amount"
                                            value={
                                                formatCurrency(settleTxnSummary?.TxnDetails?.amount) ||
                                                GLOBAL_CONSTANTS.NA
                                            }
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>
                        <div className="txn-remarks-container">
                            <DealDetailNoSpace
                                label="Transaction Remarks"
                                value={settleTxnSummary?.TxnDetails?.remarks}
                            />
                        </div>
                        <Flex
                            align="center"
                            justify="space-between"
                            wrap
                            gap={12}
                            style={{ marginTop: 20 }}
                        >
                            <Text strong>List of Available Deals to Settle</Text>
                            <Flex align="center" gap={16} wrap>
                                <Tooltip
                                    title={
                                        canViewBreakdown
                                            ? "View settlement breakdown for all selected deals"
                                            : "Select deals with open balance ≥ 10,000 to enable view"
                                    }
                                >
                                    <span>
                                        <CustomButton
                                            type="default"
                                            onClick={handleViewSelectedBreakdown}
                                            disabled={!canViewBreakdown}
                                        >
                                            View Settlement Breakdown
                                        </CustomButton>
                                    </span>
                                </Tooltip>
                                <Text
                                    strong
                                    style={{ cursor: "pointer" }}
                                    onClick={() => openModal("settleDeal")}
                                >
                                    How to Settle the deal? <InfoCircleOutlined rotate={180} />
                                </Text>
                            </Flex>
                        </Flex>
                        <SettleDealTable
                            data={settleTxnSummary}
                            selectedRowKeys={selectedRowKeys}
                            setSelectedRowKeys={setSelectedRowKeys}
                        />
                        {selectedRowKeys.length > 0 && leftoverToBusiness > 1 && (
                            <Alert
                                type="info"
                                showIcon
                                style={{ marginTop: 16 }}
                                message={`Leftover of ${formatCurrency(leftoverToBusiness)} will remain in the business account (no other open deals to settle).`}
                            />
                        )}
                        {selectedRowKeys.length > 0 && remAvailAmtToSettle > 1 && !allDealsFullySettled && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginTop: 16 }}
                                message={`${formatCurrency(remAvailAmtToSettle)} is still unallocated. Apply it to the other open deals before settling — leftover can stay in the business account only when no open deals remain.`}
                            />
                        )}
                        <FormFooter
                            onCancelClick={() => {
                                navigate(-1);
                            }}
                            onSubmitClick={handleSettleDeals}
                            disabled={!canSettle}
                            submitLoading={settleLoading}
                            btnName={BTN_TXT.SETTLE_SEL_DEAL}
                            btnType="settle"
                            error={error}
                            hasPerm={userPrivileges.includes(PERM_CODE.SUSSE)}
                        />
                    </div>
                </Spin>
                {modal.type && (
                    <InfoModal
                        title={
                            <Text style={{ fontSize: 20 }}>
                                {modal.type === "settlePreview"
                                    ? previewTitle
                                    : "How to Settle the deal"}
                            </Text>
                        }
                        open
                        centered
                        width={modal.type === "settlePreview" ? ROLE_MODAL_WIDTHS : undefined}
                        onCancel={closeModal}
                        content={
                            <>
                                {modal.type === "settlePreview" ? (
                                    <SettleDealPreview breakup={multiSettleBreakUp} />
                                ) : (
                                    <Steps
                                        className="settle-howto-steps"
                                        current={-1}
                                        direction="vertical"
                                        items={[
                                            {
                                                title:
                                                    "Select the repayment transaction and choose the deal(s) to be settled.",
                                            },
                                            {
                                                title:
                                                    "Click Settle Selected Deals to distribute the settlement amount to the Investor and Karncy as per the settlement calculation.",
                                            },
                                            {
                                                title:
                                                    "If any balance remains after settlement, it will automatically be utilized to settle other eligible pending deals of the same Business.",
                                            },
                                        ]}
                                    />
                                )}
                            </>
                        }
                    />
                )}
            </CustomCard>
        </div>
    );
};

export default SettlementDetails;
